type Request = {
    resolve: (value: unknown) => void;
    reject: (value: unknown) => void;
    fnToCall: (...args: unknown[]) => Promise<unknown>;
    args: unknown[];
    priorizationArgs?: number[];
};

const prioritizeRequestFunction = (a: Request, b: Request): number => {
    const aPriorities = a.priorizationArgs;
    const bPriorities = b.priorizationArgs;
    if (!aPriorities || !bPriorities) {
        return 0;
    }
    if (aPriorities.length !== bPriorities.length) {
        throw new Error(
            'priorizationArgs must have the same length for all requests',
        );
    }
    const firstDifferenceIndex = aPriorities.findIndex(
        (element, index) => element !== bPriorities[index],
    );
    if (firstDifferenceIndex === -1) {
        return 0;
    }
    return (
        aPriorities[firstDifferenceIndex] - bPriorities[firstDifferenceIndex]
    );
};

export default class Semaphore {
    currentRequests: Request[];

    runningRequests: number;

    maxConcurrentRequests: number;

    /**
     * Creates a semaphore that limits the number of concurrent Promises being handled
     * @param {*} maxConcurrentRequests max number of concurrent promises being handled at any time
     */
    constructor(maxConcurrentRequests = 1) {
        this.currentRequests = [];
        this.runningRequests = 0;
        this.maxConcurrentRequests = maxConcurrentRequests;
    }

    /**
     * Returns a Promise that will eventually return the result of the function passed in
     * Use this to limit the number of concurrent function executions
     * @param {*} fnToCall function that has a cap on the number of concurrent executions
     * @param  {...any} args any arguments to be passed to fnToCall
     * @returns Promise that will resolve with the resolved value as if the function passed in was directly called
     */
    callFunction(
        fnToCall: Request['fnToCall'],
        priorizationArgs: Request['priorizationArgs'],
        ...args: Request['args']
    ) {
        return new Promise((resolve, reject) => {
            this.currentRequests.push({
                resolve,
                reject,
                fnToCall,
                args,
                priorizationArgs,
            });
            this.currentRequests.sort(prioritizeRequestFunction);
            this.tryNext();
        });
    }

    tryNext() {
        if (
            this.currentRequests.length > 0 &&
            this.runningRequests < this.maxConcurrentRequests
        ) {
            const { resolve, reject, fnToCall, args } =
                this.currentRequests.shift()!;
            this.runningRequests += 1;
            const req = fnToCall(...args);
            req.then((res) => resolve(res))
                .catch((err) => reject(err))
                .finally(() => {
                    this.runningRequests -= 1;
                    this.tryNext();
                });
        }
    }
}
